<template>
  <div class="aic">
    <div v-if="loading" class="aic__notice">Loading your conversation…</div>

    <template v-else>
      <slot v-if="!messages.length" name="empty" />

      <div v-for="m in messages" :key="m.id || m.key" class="aic__row" :class="`is-${m.role}`">
        <div v-if="m.rendering" class="aic__bubble aic__rendering">
          <span class="aic__spinner" aria-hidden="true"></span>
          <div>
            <strong>{{ m.product ? `Creating the picture of the ${m.product.name} in your room…` : (m.label || 'Drawing your design…') }}</strong>
            <span>This takes about 30 seconds.</span>
          </div>
        </div>
        <div v-else class="aic__bubble" :class="{ 'is-error': m.status === 'error', 'is-render': m.payload?.render }">
          <figure v-if="m.payload?.render && m.attachments?.[0]?.url" class="aic__render">
            <a :href="m.attachments[0].url" target="_blank" rel="noopener" class="aic__render-link"><img :src="m.attachments[0].url" :alt="`${m.payload.render.product?.name} in your room`" /></a>
            <figcaption>
              <span>{{ m.payload.render.product?.name }} · AI picture</span>
              <span class="aic__figure-actions">
                <a :href="m.attachments[0].url" target="_blank" rel="noopener">Open full size</a>
                <button type="button" class="aic__save" @click="$emit('download', { message: m, index: 0 })">
                  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3v12M7 11l5 5 5-5M5 20h14" /></svg>
                  Save picture
                </button>
              </span>
            </figcaption>
            <div v-if="renderable && m.payload.render.mode === 'photo' && m.payload.render.product" class="aic__move-wrap">
              <div class="aic__move-chips">
                <button v-for="chip in MOVE_CHIPS" :key="chip" type="button" :disabled="rendering" @click="moves[m.id] = chip">{{ chip }}</button>
              </div>
              <form class="aic__move" @submit.prevent="moveIt(m)">
                <input v-model="moves[m.id]" type="text" maxlength="200" placeholder="Change it: where it stands, which way it faces, its size…" :disabled="rendering" />
                <button type="submit" :disabled="rendering || !(moves[m.id] || '').trim()">Redo picture</button>
              </form>
            </div>
          </figure>
          <figure v-else-if="m.payload?.design && m.attachments?.[0]?.url" class="aic__render aic__design">
            <a :href="m.attachments[0].url" target="_blank" rel="noopener" class="aic__render-link"><img :src="m.attachments[0].url" :alt="`Design, version ${m.payload.design.version}`" /></a>
            <figcaption>
              <span>{{ m.payload.design.source === 'photo' ? 'Your room, changed' : 'Your design' }} · version {{ m.payload.design.version }}<template v-if="m.payload.design.view_label"> · {{ m.payload.design.view_label.toLowerCase() }}</template> · AI picture</span>
              <span class="aic__figure-actions">
                <a :href="m.attachments[0].url" target="_blank" rel="noopener">Open full size</a>
                <button type="button" class="aic__save" @click="$emit('download', { message: m, index: 0 })">
                  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3v12M7 11l5 5 5-5M5 20h14" /></svg>
                  Save picture
                </button>
              </span>
            </figcaption>
            <p v-if="m.payload.design.hidden_note" class="aic__hidden-note">{{ m.payload.design.hidden_note }}</p>
            <ol v-if="m.payload.design.brief?.length" class="aic__brief">
              <li v-for="(line, i) in m.payload.design.brief" :key="i">{{ line }}</li>
            </ol>
            <!-- Another camera position: the only way to check a part that faces away from the viewer. -->
            <div v-if="!locked && m.payload.design.angles?.length" class="aic__angles">
              <span class="aic__angles-label">See it:</span>
              <button
                v-for="a in m.payload.design.angles"
                :key="a.key"
                type="button"
                class="aic__angle"
                :class="{ 'is-suggested': a.key === m.payload.design.suggested_angle }"
                :disabled="rendering || sending || a.key === m.payload.design.view"
                :title="`Draw the same design ${a.label.toLowerCase()} — costs one picture`"
                @click="$emit('design-view', { message: m, angle: a.key })"
              >{{ a.label }}</button>
            </div>
            <div v-if="!locked" class="aic__move-wrap">
              <form class="aic__move" @submit.prevent="refineIt(m)">
                <input v-model="moves[m.id]" type="text" maxlength="800" placeholder="Change it: e.g. white oak instead of grey, add an island, gold handles…" :disabled="rendering || sending" />
                <button type="submit" :disabled="rendering || sending || !(moves[m.id] || '').trim()">Update picture</button>
              </form>
              <button type="button" class="aic__quote-btn" :disabled="rendering || sending" @click="$emit('design-quote', m)">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" /><path d="M14 3v6h6M8 13h8M8 17h5" /></svg>
                Request a quotation for this design
              </button>
            </div>
          </figure>
          <div v-else-if="m.attachments?.length" class="aic__photos" :class="{ 'aic__photos--solo': m.content === '(photo)' }">
            <a v-for="(file, i) in m.attachments" :key="i" :href="file.url || '#'" target="_blank" rel="noopener" class="aic__photo" :title="file.source_title || file.name" @click="!file.url && $event.preventDefault()">
              <img v-if="file.url" :src="file.url" :alt="file.name" />
              <span v-else>{{ file.name }}</span>
              <em v-if="file.source" class="aic__photo-source">{{ file.source }}</em>
            </a>
          </div>
          <div v-if="m.content !== '(photo)'" class="aic__text" v-html="renderChatMarkup(m.content)"></div>

          <div v-if="photoOf(m)" class="aic__room">
            <span class="aic__kind">{{ kindLabel(photoOf(m).kind) }}</span>
            <template v-if="photoOf(m).palette?.length">
              <span v-for="hex in photoOf(m).palette" :key="hex" class="aic__swatch" :style="{ backgroundColor: hex }" :title="hex"></span>
            </template>
            <em>{{ [...(photoOf(m).style_tags || []), photoOf(m).wood_tone && photoOf(m).wood_tone !== 'none' ? `${photoOf(m).wood_tone} wood` : null].filter(Boolean).join(' · ') || photoOf(m).summary }}</em>
            <p v-if="photoOf(m).details" class="aic__details">{{ photoOf(m).details }}</p>
          </div>

          <button v-if="m.payload?.quote_draft && !m.payload.quote_draft.design_message_id && !locked" type="button" class="aic__draw-btn" :disabled="rendering || sending" title="Make a picture of this idea (uses 1 of your pictures for today)" @click="$emit('draw-idea', m)">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l1.8 4.7 4.7 1.8-4.7 1.8L12 16l-1.8-4.7L5.5 9.5l4.7-1.8z" /><path d="M5 19l1-2.5L8.5 15.5 6 16.5z" /></svg>
            Draw this idea for me
          </button>
          <QuoteDraftCard v-if="m.payload?.quote_draft" :message="m" :draft="m.payload.quote_draft" :signed-in="signedIn" :user="user" :send="sendQuote" @navigate="$emit('navigate')" />

          <div v-if="m.payload?.products?.length" class="aic__products">
            <div v-for="p in m.payload.products" :key="p.id" class="aic__product-wrap">
              <router-link :to="p.path" class="aic__product" @click="$emit('navigate')">
                <img v-if="p.image" :src="p.image" :alt="p.name" loading="lazy" />
                <span v-else class="aic__product-noimg" aria-hidden="true"></span>
                <span class="aic__product-meta">
                  <strong>{{ p.name }}</strong>
                  <em>{{ p.category ? `${p.category} · ` : '' }}from {{ formatPrice(p.price) }}</em>
                </span>
              </router-link>
              <button v-if="renderable && (p.image || p.model_glb_url)" type="button" class="aic__render-btn" :disabled="rendering" :title="p.model_glb_url ? 'Place the 3D model in your photo, then get a realistic picture' : 'Get a picture of this piece in your room'" @click="$emit('render', p)">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l1.8 4.7 4.7 1.8-4.7 1.8L12 16l-1.8-4.7L5.5 9.5l4.7-1.8z" /></svg>
                {{ p.model_glb_url ? 'Place in my room (3D)' : 'Show in my room' }}
              </button>
            </div>
          </div>

          <router-link v-if="m.payload?.action && !(m.payload.quote_draft && m.payload.action.key === 'request_quotation')" :to="m.payload.action.path" class="aic__action" @click="$emit('navigate')">
            {{ m.payload.action.label }}
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
          </router-link>
        </div>
        <time class="aic__time">{{ formatTime(m.created_at) }}</time>
      </div>

      <ChatLimitCard v-if="limit" :limit="limit" :countdown="countdown" :signed-in="signedIn" class="aic__limit" @navigate="$emit('navigate')" @new="$emit('new')" />

      <div v-if="!sending && !limit?.locks && followUps.length" class="aic__chips">
        <button v-for="q in followUps" :key="q" type="button" class="aic__chip" @click="$emit('pick', q)">{{ q }}</button>
      </div>

      <div v-if="sending" class="aic__row is-assistant">
        <div class="aic__bubble aic__typing" aria-label="The assistant is typing"><span></span><span></span><span></span></div>
        <time class="aic__time">{{ typingLabel }}</time>
      </div>

      <p v-if="error" class="aic__error">
        {{ error }}
        <button v-if="canRetry" type="button" @click="$emit('retry')">Try again</button>
      </p>
    </template>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, reactive } from 'vue';
import ChatLimitCard from '@/components/ai/chat-limit-card.vue';
import QuoteDraftCard from '@/components/ai/quote-draft-card.vue';
import { formatPrice } from '@/lib/storefront-api';
import { renderChatMarkup } from '@/lib/chat-markup';

defineProps({
  messages: { type: Array, default: () => [] },
  followUps: { type: Array, default: () => [] },
  sending: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
  error: { type: String, default: '' },
  canRetry: { type: Boolean, default: false },
  typingLabel: { type: String, default: '' },
  limit: { type: Object, default: null },
  countdown: { type: String, default: '' },
  signedIn: { type: Boolean, default: false },
  renderable: { type: Boolean, default: false },
  rendering: { type: Boolean, default: false },
  user: { type: Object, default: null },
  locked: { type: Boolean, default: false },
  sendQuote: { type: Function, default: null }, // (message, payload) => Promise — sends the drafted quotation request
});
const emit = defineEmits(['pick', 'retry', 'navigate', 'new', 'render', 'move', 'refine', 'design-quote', 'draw-idea', 'design-view', 'download']);

// "Move it" wishes typed under a picture, keyed by message id; the redo costs one room picture.
const moves = reactive({});
const MOVE_CHIPS = ['Turn it to face me', 'Turn it 90°', 'Seen from the back', 'A bit to the left', 'A bit to the right', 'Smaller', 'Bigger'];
function refineIt(m) {
  const text = (moves[m.id] || '').trim();
  if (!text) return;
  emit('refine', { message: m, text });
  moves[m.id] = '';
}
function moveIt(m) {
  const placement = (moves[m.id] || '').trim();
  if (!placement) return;
  emit('move', { product: m.payload.render.product, placement });
  moves[m.id] = '';
}

const KINDS = { room: 'Room photo', floor_plan: 'Floor plan', furniture: 'Furniture photo', sketch: 'Sketch', material: 'Material sample', other: 'Photo' };
const photoOf = (m) => m.payload?.photo || (m.payload?.room ? { kind: 'room', ...m.payload.room } : null);
const kindLabel = (kind) => KINDS[kind] || 'Photo';

function formatTime(value) {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
</script>

<style>
.aic { display: flex; flex-direction: column; gap: 12px; }
.aic__notice { font-size: 13px; color: #8a9197; text-align: center; padding: 20px 0; }
.aic__limit { align-self: stretch; margin: 4px 0; }
.aic__row { display: flex; flex-direction: column; max-width: 88%; }
.aic__row.is-user { align-self: flex-end; align-items: flex-end; }
.aic__row.is-assistant { align-self: flex-start; align-items: flex-start; }
.aic__bubble { padding: 10px 13px; border-radius: 16px; font-size: 14px; line-height: 1.55; background: #fff; color: #172430; border: 1px solid #e8e9ea; border-top-left-radius: 6px; }
.is-user .aic__bubble { background: #bb976d; color: #fff; border-color: #bb976d; border-radius: 16px; border-top-right-radius: 6px; }
.aic__bubble.is-error { border-color: #f0b4ae; }
.aic__text { margin: 0; overflow-wrap: anywhere; }
.aic__text p { margin: 0; }
.aic__text p + p, .aic__text ol + p, .aic__text ul + p, .aic__text p + ol, .aic__text p + ul { margin-top: 8px; }
.aic__text ol, .aic__text ul { margin: 0; padding-left: 20px; }
.aic__text ol { list-style: decimal; }
.aic__text ul { list-style: disc; }
.aic__text li { margin: 3px 0; }
.aic__text strong { font-weight: 600; }
.aic__sub { color: #8a9197; font-size: 13px; }
.dark .aic__sub { color: #a3aab0; }
.aic__time { font-size: 11px; color: #a3aab0; margin-top: 3px; padding: 0 4px; }

.aic__photos { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 8px; }
.aic__photos--solo { margin-bottom: 0; }
.aic__photo { position: relative; display: block; border-radius: 10px; overflow: hidden; background: rgba(255, 255, 255, 0.15); }
.aic__photo-source { position: absolute; left: 6px; bottom: 6px; padding: 2px 7px; border-radius: 999px; background: rgba(23, 36, 48, 0.8); color: #fff; font-size: 10.5px; font-style: normal; font-weight: 600; letter-spacing: 0.02em; pointer-events: none; }
.aic__photo img { display: block; width: 150px; height: 110px; object-fit: cover; }
.aic__photo span { display: block; padding: 8px 10px; font-size: 12px; }

.aic__room { display: flex; align-items: center; flex-wrap: wrap; gap: 5px; margin-top: 10px; padding: 8px 10px; border-radius: 10px; background: #faf8f5; border: 1px solid #ece7df; font-size: 12px; }
.aic__room em { font-style: normal; color: #8a9197; margin-left: 4px; }
.aic__kind { font-size: 11px; font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase; color: #bb976d; margin-right: 4px; }
.aic__details { flex-basis: 100%; margin: 4px 0 0; font-size: 12px; line-height: 1.45; color: #3c474e; }
.dark .aic__details { color: #cfd4d8; }
.aic__swatch { width: 18px; height: 18px; border-radius: 999px; border: 1px solid rgba(23, 36, 48, 0.12); }

.aic__products { display: grid; gap: 6px; margin-top: 10px; }
.aic__product-wrap { display: flex; flex-direction: column; gap: 4px; }
.aic__render-btn { align-self: flex-start; display: inline-flex; align-items: center; gap: 5px; padding: 5px 10px; border-radius: 999px; border: 1px solid #dccdb9; background: #fff; color: #172430; font-size: 12px; font-weight: 600; cursor: pointer; }
.aic__render-btn:hover { border-color: #bb976d; color: #bb976d; }
.aic__render-btn:disabled { opacity: 0.5; cursor: wait; }
.aic__rendering { display: flex; align-items: center; gap: 12px; }
.aic__rendering strong { display: block; font-size: 13.5px; }
.aic__rendering span:last-child { font-size: 12.5px; color: #8a9197; }
.aic__spinner { flex: 0 0 22px; width: 22px; height: 22px; border-radius: 999px; border: 3px solid rgba(187, 151, 109, 0.25); border-top-color: #bb976d; animation: aic-spin 0.9s linear infinite; }
@keyframes aic-spin { to { transform: rotate(360deg); } }
.aic__bubble.is-render { padding: 8px; }
.aic__render { margin: 0; }
/* Fits on one screen: never taller than ~55 % of the viewport, never wider than the bubble. */
.aic__render-link { display: block; width: fit-content; max-width: 100%; }
.aic__render img { display: block; width: auto; max-width: 100%; max-height: min(55vh, 460px); border-radius: 12px; }
.aic__move-wrap { margin: 4px 6px 2px; }
.aic__move-chips { display: flex; flex-wrap: wrap; gap: 5px; margin-bottom: 6px; }
.aic__move-chips button { height: 26px; padding: 0 10px; border-radius: 999px; border: 1px solid #e8e9ea; background: #f6f2ec; color: #3c474e; font-size: 11.5px; cursor: pointer; }
.aic__move-chips button:hover:not(:disabled) { border-color: #bb976d; color: #bb976d; }
.aic__move-chips button:disabled { opacity: 0.5; cursor: default; }
.aic__move { display: flex; gap: 6px; }
.aic__figure-actions { display: inline-flex; align-items: center; gap: 10px; }
.aic__save { display: inline-flex; align-items: center; gap: 4px; border: 0; background: none; padding: 0; font: inherit; font-size: 12px; color: #bb976d; cursor: pointer; text-decoration: underline; text-underline-offset: 2px; }
.aic__save:hover { color: #172430; }
.dark .aic__save { color: #dcc3a1; }
.dark .aic__save:hover { color: #fff; }
.aic__hidden-note { margin: 6px 0 0; font-size: 12.5px; line-height: 1.45; color: #6b5b46; background: rgba(187, 151, 109, 0.12); border-radius: 8px; padding: 7px 10px; }
.aic__angles { display: flex; flex-wrap: wrap; align-items: center; gap: 5px; margin-top: 8px; }
.aic__angles-label { font-size: 11.5px; font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase; color: #8a9197; margin-right: 2px; }
.aic__angle { font-size: 12px; line-height: 1.2; padding: 6px 10px; border-radius: 999px; border: 1px solid #dccdb9; background: #fff; color: #172430; cursor: pointer; }
.aic__angle:hover:not(:disabled) { background: rgba(187, 151, 109, 0.14); border-color: #bb976d; }
.aic__angle.is-suggested { border-color: #bb976d; background: rgba(187, 151, 109, 0.16); font-weight: 600; }
.aic__angle:disabled { opacity: 0.45; cursor: default; }
.dark .aic__hidden-note { color: #e8d9c4; background: rgba(187, 151, 109, 0.18); }
.dark .aic__angle { background: #22313d; border-color: #3c474e; color: #fff; }

.aic__brief { margin: 6px 6px 2px; padding-left: 22px; font-size: 12.5px; color: #3c474e; }
.aic__brief li { margin: 2px 0; }
.aic__brief li:last-child { font-weight: 600; color: #172430; }
.aic__draw-btn { display: inline-flex; align-items: center; gap: 6px; margin: 8px 6px 0; height: 32px; padding: 0 12px; border-radius: 999px; border: 1px solid #dccdb9; background: #fff; color: #8a6a3e; font-size: 12.5px; font-weight: 600; cursor: pointer; }
.aic__draw-btn:hover:not(:disabled) { background: #bb976d; border-color: #bb976d; color: #fff; }
.aic__draw-btn:disabled { opacity: 0.5; cursor: default; }
.dark .aic__draw-btn { background: #22313d; border-color: #3c474e; color: #dccdb9; }
.aic__quote-btn { display: inline-flex; align-items: center; gap: 6px; margin-top: 8px; height: 34px; padding: 0 14px; border-radius: 999px; border: 0; background: #172430; color: #fff; font-size: 12.5px; font-weight: 600; cursor: pointer; }
.aic__quote-btn:hover:not(:disabled) { background: #bb976d; }
.aic__quote-btn:disabled { opacity: 0.5; cursor: default; }
.aic__move input { flex: 1; min-width: 0; height: 34px; padding: 0 10px; border: 1px solid #dfe2e4; border-radius: 999px; font-size: 12.5px; color: #172430; background: #fff; }
.aic__move input:focus { outline: none; border-color: #bb976d; }
.aic__move button { height: 34px; padding: 0 12px; border-radius: 999px; border: 1px solid #dccdb9; background: #fff; color: #172430; font-size: 12px; font-weight: 600; cursor: pointer; white-space: nowrap; }
.aic__move button:hover:not(:disabled) { border-color: #bb976d; color: #bb976d; }
.aic__move button:disabled { opacity: 0.5; cursor: default; }
.aic__render figcaption { display: flex; justify-content: space-between; gap: 10px; padding: 8px 6px 2px; font-size: 12px; color: #8a9197; }
.aic__render figcaption a { color: #bb976d; text-decoration: underline; }
.aic__bubble.is-render .aic__text { padding: 6px 6px 0; font-size: 13px; }
.aic__product { display: flex; align-items: center; gap: 10px; padding: 6px; border-radius: 10px; background: #faf8f5; border: 1px solid #ece7df; color: inherit; text-decoration: none; transition: border-color 0.15s ease; }
.aic__product:hover { border-color: #bb976d; }
.aic__product img, .aic__product-noimg { flex: 0 0 52px; width: 52px; height: 52px; border-radius: 8px; object-fit: cover; background: #e8e9ea; }
.aic__product-meta { display: flex; flex-direction: column; min-width: 0; line-height: 1.3; }
.aic__product-meta strong { font-size: 13px; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.aic__product-meta em { font-style: normal; font-size: 12px; color: #8a9197; }

.aic__action { display: inline-flex; align-items: center; gap: 6px; margin-top: 10px; padding: 8px 12px; border-radius: 999px; background: #172430; color: #fff !important; font-size: 12.5px; font-weight: 600; text-decoration: none; }
.aic__action:hover { background: #bb976d; }

.aic__chips { display: flex; flex-wrap: wrap; gap: 6px; padding-left: 4px; }
.aic__chip { font-size: 12.5px; line-height: 1.3; padding: 7px 11px; border-radius: 999px; border: 1px solid #dccdb9; background: #fff; color: #172430; cursor: pointer; text-align: left; transition: background 0.15s ease; }
.aic__chip:hover { background: rgba(187, 151, 109, 0.12); border-color: #bb976d; }

.aic__typing { display: flex; gap: 4px; padding: 12px 14px; }
.aic__typing span { width: 7px; height: 7px; border-radius: 999px; background: #bb976d; opacity: 0.4; animation: aic-dot 1.2s infinite ease-in-out; }
.aic__typing span:nth-child(2) { animation-delay: 0.15s; }
.aic__typing span:nth-child(3) { animation-delay: 0.3s; }
@keyframes aic-dot { 0%, 80%, 100% { opacity: 0.3; transform: translateY(0); } 40% { opacity: 1; transform: translateY(-3px); } }

.aic__error { font-size: 12.5px; color: #b42318; margin: 0; padding: 0 4px; }
.aic__error button { margin-left: 6px; text-decoration: underline; color: inherit; background: none; border: 0; cursor: pointer; font-size: inherit; }

.dark .aic__bubble { background: #1e2a35; border-color: #3c474e; color: #e9edf0; }
.dark .is-user .aic__bubble { background: #bb976d; border-color: #bb976d; color: #fff; }
.dark .aic__room, .dark .aic__product { background: #22313d; border-color: #3c474e; color: #fff; }
.dark .aic__product-meta em, .dark .aic__room em { color: #a3aab0; }
.dark .aic__chip { background: #22313d; border-color: #3c474e; color: #fff; }
.dark .aic__render-btn { background: #22313d; border-color: #3c474e; color: #fff; }
.dark .aic__move input, .dark .aic__move button, .dark .aic__move-chips button { background: #22313d; border-color: #3c474e; color: #fff; }
.dark .aic__brief, .dark .aic__brief li:last-child { color: #dfe2e4; }
.dark .aic__chip:hover { border-color: #bb976d; background: rgba(187, 151, 109, 0.18); }
.dark .aic__action { background: #bb976d; }
.dark .aic__action:hover { background: #fff; color: #172430 !important; }
</style>
